import { useRef, useState } from "react"
import { Badge, getBadgeClass } from "@oneethos/shared"
import './upload-slot.scss'
import { Spinner, SpinnerSize } from "@fluentui/react"
import { AiLoadingIndicator } from "../ai-loading-indicator"
import ContractIssueResolverModal from "../contract-issue-resolver-modal"
import ImageUploader from "../image-uploader"
import CheckboxIcon from "../checkbox-icon"
import {
  LoanDocument,
  MimeTypes,
  RequiredDocConfig,
  AgentDocResult,
  Agents,
  SolarDocStatus,
  getDisplayLabel,
  StatusData,
  ChecklistOwner
} from "@oneethos/shared"

type DocStatusProps = {
  doc?: LoanDocument
  showRespond?: boolean
  setShowRespond?: (show: boolean) => void
  onChange?: (doc: LoanDocument, status?: SolarDocStatus) => void
  onResponseToAgent?: (doc: LoanDocument) => void
  bankerComments?: string
}

const DocFeedback = ({
  doc = {} as LoanDocument,
  onResponseToAgent,
  showRespond,
  setShowRespond
}: DocStatusProps) => {
  const [installerResponse, setInstallerResponse] = useState(doc.installerResponse)
  const isAgentAprroved = doc.agentResult === AgentDocResult.Approve

  return (
    <div className="mt-2">
      {doc.agentComments ?
        <div>
          <div className={`small ${isAgentAprroved ? 'text-success' : 'text-danger'}`}>
            <b>AI Comments:</b> {doc.agentComments}
          </div>
          {doc.installerResponse ?
            <div className="mt-2 small">
              <b>Response:</b>
              <span>{doc.installerResponse}</span>
            </div> : null}
          {showRespond ? <div>
            <textarea
              className="form-control mt-3"
              placeholder="Add a comment..."
              rows={3}
              value={installerResponse}
              autoFocus
              onChange={ev => setInstallerResponse(ev.target.value)}
            />
            <div className="mt-2">
              <button type="button" onClick={() => {
                setShowRespond(false)
                setInstallerResponse('')
              }} className="btn btn-tiny btn-secondary">
                Cancel
              </button>&nbsp;
              <button type="button" onClick={() => {
                setShowRespond(false)
                onResponseToAgent({ ...doc, installerResponse })
              }} className="btn btn-tiny btn-primary">
                Submit Response
              </button>
            </div>
          </div> : null}
        </div> : null}
    </div>
  )
}

type UploadSlotProps = {
  doc?: LoanDocument & File
  multiple?: boolean
  slotConfig: RequiredDocConfig // TODO: update type
  onChange: (doc: LoanDocument | File, status?: SolarDocStatus) => void
  onProjectChange?: (project: StatusData) => void
  onResponseToAgent?: (doc: LoanDocument) => void
  bankerComments?: string
  project?: StatusData
  handleRowToggle?: () => void,
  isCompleted?: boolean
  dateField?: string
  status?: string
  checklistOwner: ChecklistOwner
}

export const UploadSlot = ({
  doc,
  multiple = false,
  slotConfig,
  onChange,
  onResponseToAgent,
  handleRowToggle,
  bankerComments,
  onProjectChange,
  project,
  isCompleted,
  dateField,
  status,
  checklistOwner
}: UploadSlotProps) => {
  const activeAgentRuns = doc?.agentWorkflowRuns?.filter(r => Agents.WorkflowRun.isActive(r))
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showContractModal, setShowContractModal] = useState(false)
  const isActiveAgentRuns = activeAgentRuns?.length > 0
  const [showRespond, setShowRespond] = useState(false)
  const maybeNeedsDoc = [
    AgentDocResult.AwaitingQuestion,
    AgentDocResult.Deny
  ].includes(doc?.agentResult) && !doc?.installerResponse

  const isContract = slotConfig.slot === 'Signed Contract'
  const isDocumentUploaded = doc?.status === SolarDocStatus.PendingUpload || doc?.status === SolarDocStatus.Uploaded
  const commentFieldLabel = isContract ? 'Banker Comments' : 'Funding Team Comments'

  const uploadStatusIndicator = () => {
    return isDocumentUploaded
      ? <Badge variant="secondary" outline>Submitting...&nbsp;<Spinner size={SpinnerSize.small} /></Badge>
      : <div className={`badge fs-12 text-capitalize rounded-pill ${getBadgeClass(status)}`}>{status}</div>
  }

  const statusIndicator = () => {
    return isActiveAgentRuns
      ? <AiLoadingIndicator run={activeAgentRuns[0]} />
      : uploadStatusIndicator()
  }

  const validateHandleRowToggle = () => {
    if (!isActiveAgentRuns && !isDocumentUploaded) {
      handleRowToggle()
    }
  }

  return (
    <tr className='align-middle'>
      <td style={{ width: '30px' }}>
        <CheckboxIcon isCompleted={isCompleted} checklistOwner={checklistOwner} />
      </td>
      <td style={{ maxWidth: '300px' }}>
        <div onClick={validateHandleRowToggle} className="pointer">
          <div className="fw-bold mb-1 text-dark" >{getDisplayLabel(slotConfig.slot)}</div>
          <div className="text-muted small">{slotConfig.instructions}</div>
        </div>
        {bankerComments ?
          <div className='text-danger small'>
            <b>{commentFieldLabel}:</b> {bankerComments}
          </div> :
          <DocFeedback
            doc={doc}
            onChange={onChange}
            onResponseToAgent={onResponseToAgent}
            showRespond={showRespond}
            setShowRespond={setShowRespond}
          />
        }
        {(
          (doc?.status === SolarDocStatus.Comments || maybeNeedsDoc) &&
          !showRespond
        ) ? (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-primary btn-tiny"
              onClick={() => isContract ? setShowContractModal(true) : fileInputRef.current?.click()}>
              Upload New Document
            </button>
            {doc?.status !== SolarDocStatus.Comments ? <button type="button"
              className="btn btn-link btn-tiny ms-2"
              onClick={() => setShowRespond(true)}>Respond to AI Feedback</button> : null}
          </div>) : null}
      </td>
      <td className="text-center">{dateField || ''}</td>
      <td className="text-center">
        {['Other', 'Change Order Docs'].includes(slotConfig.slot) ? null :
          <div className="doc-status-label">
            {statusIndicator()}
          </div>}
      </td>
      <td className="text-center">
        {isContract ?
          <div className="d-flex flex-column align-items-center">
            <ImageUploader
              onClick={() => setShowContractModal(true)}
              doc={doc}
              ref={fileInputRef}
            />
            <div className="mt-2">
              <button type="button"
                className="btn btn-primary btn-tiny"
                onClick={() => setShowContractModal(true)}>Edit System Details</button>
            </div>
            {showContractModal &&
              <ContractIssueResolverModal
                project={project}
                onClose={() => setShowContractModal(false)}
                onChange={onProjectChange}
              />}
          </div> :
          <ImageUploader
            onChange={onChange}
            acceptContentTypes={MimeTypes.allowedUploadExtensions().join(',')}
            doc={doc}
            ref={fileInputRef}
          />}
      </td>
    </tr>
  )
}
